/**
 * Drum Kit configuration model + SVG generation.
 *
 * A "Drum Kit" is a SINGLE stage element (iconId: 'drumkit') whose
 * detailed contents are described by `DrumKitConfig` stored in
 * StageElement.config. The on-stage graphic is generated (FRONT-VIEW,
 * audience perspective) from this config, so the kit image always
 * matches the chosen pieces, handedness and cymbal layout.
 *
 * All coordinates are in a 0..100 viewBox space (square), so the same
 * generator feeds both the editor preview and the on-stage render.
 */

export type DrumPieceType =
  | 'kick'
  | 'snare'
  | 'rackTom'
  | 'floorTom'
  | 'hihat'
  | 'ride'
  | 'crash'
  | 'china'
  | 'splash'
  | 'cowbell';

export const DRUM_PIECES: DrumPieceType[] = [
  'kick',
  'snare',
  'rackTom',
  'floorTom',
  'hihat',
  'ride',
  'crash',
  'china',
  'splash',
  'cowbell',
];

export const PIECE_LABELS: Record<DrumPieceType, string> = {
  kick: 'Kick',
  snare: 'Snare',
  rackTom: 'Rack Tom',
  floorTom: 'Floor Tom',
  hihat: 'Hi-Hat',
  ride: 'Ride',
  crash: 'Crash',
  china: 'China',
  splash: 'Splash',
  cowbell: 'Cowbell',
};

export type Handedness = 'right' | 'left';

export interface DrumKitConfig {
  /** Count of each piece in the kit. */
  counts: Partial<Record<DrumPieceType, number>>;
  handedness: Handedness;
  /** Preset name for quick recall (free text / known id). */
  preset?: string;
}

export function defaultDrumKit(): DrumKitConfig {
  return {
    counts: {
      kick: 1,
      snare: 1,
      rackTom: 1,
      floorTom: 2,
      hihat: 1,
      ride: 1,
      crash: 2,
      china: 1,
      splash: 0,
      cowbell: 0,
    },
    handedness: 'right',
    preset: 'custom',
  };
}

export const DRUM_PRESETS: Record<string, DrumKitConfig> = {
  '4pc-standard': {
    counts: { kick: 1, snare: 1, rackTom: 1, floorTom: 1, hihat: 1, ride: 1, crash: 1 },
    handedness: 'right',
    preset: '4pc-standard',
  },
  '5pc-fusion': {
    counts: { kick: 1, snare: 1, rackTom: 2, floorTom: 1, hihat: 1, ride: 1, crash: 2, china: 1 },
    handedness: 'right',
    preset: '5pc-fusion',
  },
  '7pc-double': {
    counts: { kick: 1, snare: 1, rackTom: 3, floorTom: 2, hihat: 1, ride: 1, crash: 3, china: 1, splash: 1 },
    handedness: 'right',
    preset: '7pc-double',
  },
  'jazz-4pc': {
    counts: { kick: 1, snare: 1, rackTom: 0, floorTom: 1, hihat: 1, ride: 1, crash: 1, china: 0 },
    handedness: 'right',
    preset: 'jazz-4pc',
  },
  'lefty-5pc': {
    counts: { kick: 1, snare: 1, rackTom: 2, floorTom: 1, hihat: 1, ride: 1, crash: 2, china: 1 },
    handedness: 'left',
    preset: 'lefty-5pc',
  },
};

export function cloneDrumKit(c: DrumKitConfig): DrumKitConfig {
  return { counts: { ...c.counts }, handedness: c.handedness, preset: c.preset };
}

// ── Front-view PNG kit composer ─────────────────────────────────────
/**
 * A single drum piece placed in the 0..100 front-view kit space
 * (x right, y down). `src` is the StagePlotPro photo asset; w/h are in
 * viewBox units and preserve the asset's intrinsic aspect ratio so the
 * photo never distorts.
 */
export interface DrumKitPiece {
  key: string;
  src: string;
  x: number; // left edge (0..100)
  y: number; // top edge (0..100)
  w: number; // width (0..100)
  h: number; // height (0..100)
}

/**
 * Full-kit StagePlotPro assets (a COMPLETE, coherent drum kit in a single
 * image, unlike the individual pieces which have heterogeneous perspectives
 * and read as "scattered" when composed). Chosen by the kit's configuration:
 * rack-tom count drives the two/three/four-tom kit asset; 2+ kicks use the
 * double-kick asset. A drum mat renders underneath for a grounded look.
 */
const KIT_ASSET: { toms: number; kick: number; src: string; aspect: number }[] = [
  { toms: 4, kick: 1, src: '/stageplot-assets/drum/FourToms.png', aspect: 185 / 122 },
  { toms: 3, kick: 1, src: '/stageplot-assets/drum/ThreeToms.png', aspect: 198 / 122 },
  { toms: 2, kick: 1, src: '/stageplot-assets/drum/TwoToms.png', aspect: 171 / 110 },
  { toms: 1, kick: 1, src: '/stageplot-assets/drum/TwoToms.png', aspect: 171 / 110 },
];
const DOUBLE_KICK_ASSET = { src: '/stageplot-assets/drum/doublekick.png', aspect: 189 / 143 };
const DRUM_MAT_ASSET = { src: '/stageplot-assets/drum/drummat.png', aspect: 152 / 129 };

function kitAssetFor(cfg: DrumKitConfig): { src: string; aspect: number } {
  const kicks = cfg.counts.kick ?? 0;
  if (kicks >= 2) return DOUBLE_KICK_ASSET;
  const toms = cfg.counts.rackTom ?? 0;
  // Pick the SMALLEST complete-kit asset that covers the requested rack-tom
  // count (ascending by toms → 1-2→TwoToms, 3→ThreeToms, 4→FourToms).
  const ascending = [...KIT_ASSET].sort((a, b) => a.toms - b.toms);
  return ascending.find((k) => toms <= k.toms) ?? ascending[ascending.length - 1];
}

const fmtCoord = (n: number) => (Math.round(n * 100) / 100).toString();

/**
 * Compose a FRONT-VIEW drum kit (audience perspective) from the chosen
 * pieces' StagePlotPro photos. Coordinate space 0..100 (x right, y down;
 * y=0 is up / behind, y=100 is the floor / front row).
 *
 * ⚠️ 2026-09-04 — the kit is now a COMPLETE single StagePlotPro asset
 * (TwoToms/ThreeToms/FourToms/doublekick on a drum mat) instead of composing
 * the individual pieces. Composing the pieces gave a "scattered / exploded"
 * look because the piece photos have heterogeneous perspectives (kick front,
 * snare profile, toms top-down). A complete kit reads as one real instrument.
 * The piece-count config still drives which complete-kit asset is shown.
 */
export function buildDrumKitPieces(cfg: DrumKitConfig | null | undefined): DrumKitPiece[] {
  const safe = cfg && typeof cfg === 'object' && cfg.counts ? cfg : defaultDrumKit();
  const pieces: DrumKitPiece[] = [];

  // Drum mat under the whole kit for a grounded footprint.
  const mat = DRUM_MAT_ASSET;
  const matW = 62;
  const matH = matW / mat.aspect;
  pieces.push({ key: 'mat', src: mat.src, x: 50 - matW / 2, y: 62 - matH / 2, w: matW, h: matH });

  // Complete kit asset, centred, preserving its intrinsic aspect.
  const kit = kitAssetFor(safe);
  const w = 60;
  const h = w / kit.aspect;
  pieces.push({ key: 'kit', src: kit.src, x: 50 - w / 2, y: 55 - h / 2, w, h });

  return pieces;
}

/**
 * Build the inner SVG markup for a FRONT-VIEW drum kit. Unlike the old
 * vector builder, this now assembles the kit from the REAL StagePlotPro
 * PNGs (`<image href>`) so the composite matches the single-instrument
 * icons. `color` is accepted for signature compatibility but the photos
 * are grayscale and ignore it.
 */
export function buildDrumKitSvg(cfg: DrumKitConfig | null | undefined, _color: string): string {
  const safe = cfg && typeof cfg === 'object' && cfg.counts ? cfg : defaultDrumKit();
  const inner = buildDrumKitPieces(safe)
    .map((p) => `<image href="${p.src}" x="${fmtCoord(p.x)}" y="${fmtCoord(p.y)}" width="${fmtCoord(p.w)}" height="${fmtCoord(p.h)}"/>`)
    .join('');
  // Left-handed kits mirror the whole complete-kit asset horizontally around
  // x=50 so the cymbal/drum layout is flipped for a lefty drummer.
  if (safe.handedness === 'left') {
    return `<g transform="translate(100,0) scale(-1,1)">${inner}</g>`;
  }
  return inner;
}
/** Short human summary, e.g. "1K 1S 1RT 2FT 1HH 1R 2C 1Ch". */
export function drumKitSummary(cfg: DrumKitConfig): string {
  const order: DrumPieceType[] = [
    'kick',
    'snare',
    'rackTom',
    'floorTom',
    'hihat',
    'ride',
    'crash',
    'china',
    'splash',
    'cowbell',
  ];
  return order
    .filter((p) => (cfg.counts[p] ?? 0) > 0)
    .map((p) => `${cfg.counts[p]}${p === 'hihat' ? 'HH' : p === 'rackTom' ? 'RT' : p === 'floorTom' ? 'FT' : p[0].toUpperCase()}`)
    .join(' ');
}

/**
 * Visual bounding-box centre of the assembled kit in the 0..100 front-view
 * space. The kit is drawn from a 116×100 viewBox and top-aligned inside its
 * (usually larger) square element box, so the kit's ACTUAL centre can sit off
 * the element-box centre — especially vertically, where the drums are
 * bottom-heavy in the viewBox. Used to centre a riser on the kit's real
 * drawn footprint rather than its bounding box. Returns null when no pieces.
 */
export function drumKitVisualBox(cfg: DrumKitConfig | null | undefined): { cx: number; cy: number } | null {
  const pieces = buildDrumKitPieces(cfg);
  if (!pieces.length) return null;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of pieces) {
    if (p.x < minX) minX = p.x;
    if (p.x + p.w > maxX) maxX = p.x + p.w;
    if (p.y < minY) minY = p.y;
    if (p.y + p.h > maxY) maxY = p.y + p.h;
  }
  return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

/**
 * Serialise a DrumKitConfig to an SVG STRING (viewBox -8 0 116 100)
 * for the SVG/PDF export path. The kit is assembled from StagePlotPro
 * `<image href>` refs (see buildDrumKitSvg); callers that rasterize the
 * SVG (SVG loaded in an <img>) must inline those PNGs as data URIs first
 * (see inlineStagePlotImages) because an <img>-embedded SVG cannot fetch
 * external resources.
 */
export function drumKitToSvg(cfg: DrumKitConfig, color: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-8 0 116 100" width="100" height="100"><g>${buildDrumKitSvg(cfg, color)}</g></svg>`;
}
